/**
 * Floating widget — closed: bottom-right launcher button; open: ChatPanel.
 *
 * Renders only when BOTH conditions hold:
 *   - User is an analytics admin (`analytics_admins:` in users.yaml). The
 *     backend `/api/chat/stream` endpoint enforces the same gate; this UI
 *     hide just prevents non-admins from seeing a dead button.
 *   - Current path is a MARS route (mars-live / mars-forecasts / mars-portfolio).
 *
 * Mounted once in App.tsx so the conversation survives navigation between
 * MARS sub-pages.
 */
import { useLocation } from 'react-router-dom';

import { useAuth } from '@/lib/auth/useAuth';

import { pageFromPath } from './context';
import { usePortalChatStore } from './store';
import ChatPanel from './ChatPanel';

export default function PortalChat(): JSX.Element | null {
  const location = useLocation();
  const { isAnalyticsAdmin } = useAuth();
  const onMarsPage = pageFromPath(location.pathname) !== null;
  const open = usePortalChatStore((s) => s.open);
  const setOpen = usePortalChatStore((s) => s.setOpen);

  if (!isAnalyticsAdmin) return null;
  if (!onMarsPage) return null;

  if (open) return <ChatPanel onClose={() => setOpen(false)} />;

  return (
    <button
      type="button"
      onClick={() => setOpen(true)}
      // Use the explicit CSS variable rather than the Tailwind alias
      // (`bg-accent` was rendering faint on some browsers, hiding the
      // "?" against the same-tone background).
      className="fixed bottom-4 right-4 z-[60] flex h-12 w-12 items-center justify-center rounded-full text-white shadow-lg bg-[color:var(--accent)] hover:bg-[color:var(--accent-hover)]"
      aria-label="Open MARS assistant"
    >
      <span className="text-xl font-bold leading-none">?</span>
    </button>
  );
}
